import React, { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { FaceId } from '@vlabs/api/luna-api/model';
import {
  useGetFaces, getGetFacesQueryOptions, getPatchCardMutationOptions,
} from '@vlabs/api/point-api/client';
import type { GetFacesParams } from '@vlabs/api/point-api/model';
import type { QueryClient } from '@vlabs/api/queryClient';
import { MoreHorizontalIcon } from '@vlabs/icons';
import { permissions } from '@vlabs/shared/config';
import { Control, DropdownList } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useSelector } from 'react-redux';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import { DeleteFaceDropdownItem } from '@vlabs/monokit/features/delete-face';
import { SearchFaceButton } from '@vlabs/monokit/features/search-by-face-id';
import { SetFaceAsCardFaceDropdownItem } from '@vlabs/monokit/features/set-face-as-card-face';
import { VerifyFaceButton } from '@vlabs/monokit/features/verify-face';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { FaceCard } from '@vlabs/monokit/shared/ui/face-card';

import st from './PersonFaceCard.module.sass';

interface PersonFaceCardProps extends WithDataTestid {
  faceId: FaceId;
}

const displayName = 'PersonFaceCard';
const testid = buildTestid(displayName);

export function PersonFaceCard({
  faceId,
  'data-testid': dataTestId = undefined,
}: PersonFaceCardProps): React.JSX.Element {
  const can = useSelector(viewerCan);
  const queryClient = useQueryClient();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const getFacesParams: GetFacesParams = { face_ids: [faceId] };
  const getFace = useGetFaces(getFacesParams);
  const canDeleteFace = can([permissions.face.deletion]);
  const face = getFace.data?.faces?.[0];

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  const toggleDropdown = useCallback(() => setIsDropdownOpen((prevState) => !prevState), []);

  const hasActions = !face?.is_card_face || canDeleteFace;

  const dropdownOptions = [];

  if (!!face?.card_id && !face?.is_card_face) {
    dropdownOptions.push(<SetFaceAsCardFaceDropdownItem
      cardId={face.card_id}
      faceId={faceId}
      key={face.card_id}
      onSetFaceAsCardFace={closeDropdown}
    />);
  }

  if (canDeleteFace) {
    dropdownOptions.push(<DeleteFaceDropdownItem
      faceId={faceId}
      onDeleteFace={() => {
        if (getFace.data?.faces?.[0]?.card_id) {
          queryClient.invalidateQueries(getGetFacesQueryOptions({ card_ids: [getFace.data?.faces?.[0]?.card_id] }));
          (queryClient as unknown as QueryClient).mutate(
            getPatchCardMutationOptions(queryClient as unknown as QueryClient),
            { cardId: getFace.data.faces[0].card_id, data: { main_face_id: null } },
          );
        }
        closeDropdown();
      }}
    />);
  }

  return (
    <FaceCard
      {...testid(dataTestId, face?.face_id)}
      actions={(
        <div className={st.Container}>
          <div className={st.ButtonWrapper}>
            <SearchFaceButton
              className={st.Button}
              faceId={faceId}
            />
          </div>
          <div className={st.ButtonWrapper}>
            <VerifyFaceButton
              className={st.Button}
              faceId={faceId}
            />
          </div>
          <DropdownList
            isOpen={isDropdownOpen}
            onClose={closeDropdown}
            options={dropdownOptions}
            placement="bottom-right"
          >
            <Control.IconButton
              data-testid="faceCard.actionsButton"
              disabled={!hasActions}
              icon={<MoreHorizontalIcon />}
              kind="secondary"
              onClick={toggleDropdown}
              shape="square"
              size="small"
              variant="outlined"
            />
          </DropdownList>
        </div>
      )}
      faceId={faceId}
      key={faceId}
      shouldShowPin={face?.is_card_face}
      size="large"
    />
  );
}
